import { useEffect } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { useAccordionSection, SectionContextValue } from './PunchupAccordion';

/**
 * useAccordionForm
 *
 * Connects a react-hook-form instance to the enclosing PunchupAccordionSection.
 * Wires dirty tracking and save-overlay state once so every accordion inline form gets:
 *   - The unsaved-dot + shake-on-close guard automatically
 *   - A spinner overlay on the edit area while isSubmitting (via PunchupInlineEditor)
 *   - flash / close / scrollToSection / isOpen / isSaving without boilerplate
 *
 * Required for every accordion inline form; see PunchupAccordion/CLAUDE.md for the pattern it replaces.
 */
export const useAccordionForm = <T extends FieldValues>(
	formMethods: UseFormReturn<T>
): Omit<SectionContextValue, 'setDirty' | 'setSaving'> => {
	const { flash, close, setDirty, setSaving, scrollToSection, isOpen, isSaving } = useAccordionSection();
	const { isDirty, isSubmitting } = formMethods.formState;

	useEffect(() => {
		setDirty(isDirty);
		return () => setDirty(false);
	}, [isDirty, setDirty]);

	useEffect(() => {
		setSaving(isSubmitting);
		return () => setSaving(false);
	}, [isSubmitting, setSaving]);

	return { flash, close, scrollToSection, isOpen, isSaving };
};
